import classNames from 'classnames';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BotIcon } from '@/components/ui/bot-icon';
import { Segmented, StateMessage } from '@/components/ui/controls';
import { NotYetInfo, notYet } from '@/components/ui/not-yet';
import { Badge, Field } from '@/components/ui/page';
import { SegmentChip } from '@/components/ui/segment-chip';
import { btn, field } from '@/components/ui/styles';
import { countryName, formatDate, formatDateTime } from '@/utils/format';
import http from '@/utils/http';
import { useSegments } from '@/utils/use-segments';

import type { Audience, Channel, DeliveryResult, Message, MessageStatus, TriggerEvent } from './api-types';
import { CHANNELS, VARIABLES, channelName, fillSample } from './channels';

// Pieces the messaging pages share (OUT-1…6).

const ICONS: Record<Channel, ReactNode> = {
  email: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2.5 4.5L8 9l5.5-4.5" />
    </>
  ),
  telegram: <path d="M14.5 1.5L1.5 7l5 2 2 5 6-12.5zM6.5 9l8-7.5" />,
  popup: (
    <>
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M2 5.5h12" />
      <rect x="5" y="7.5" width="6" height="3.5" rx="0.8" />
    </>
  ),
};

/** A channel's icon: envelope, paper plane, or the Owner Hub window (OUT-1). */
export const ChannelIcon = ({ channel }: { channel: Channel }) => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {ICONS[channel]}
  </svg>
);

/** The channels a message goes out on, as icons with names (or icons only, with names on hover). */
export const ChannelList = ({ channels, iconsOnly }: { channels: Channel[]; iconsOnly?: boolean }) => (
  <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-fg-muted">
    {channels.map((c) => (
      <span key={c} className="inline-flex items-center gap-1" title={channelName(c)}>
        <ChannelIcon channel={c} />
        {iconsOnly ? <span className="sr-only">{channelName(c)}</span> : <span className="text-sm">{channelName(c)}</span>}
      </span>
    ))}
  </span>
);

/** The three channels as checkboxes, each with what it reaches (OUT-1, OUT-6, Q8). */
export const ChannelPicker = ({ value, onChange, withNotes = true }: { value: Channel[]; onChange: (v: Channel[]) => void; withNotes?: boolean }) => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
    {CHANNELS.map((c) => {
      const on = value.includes(c.id);
      // Email does not work in this version: faded, with the info mark at the top right.
      const fade = c.id === 'email' ? notYet : undefined;
      return (
        <label
          key={c.id}
          className={classNames(
            'flex min-h-11 cursor-pointer items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm shadow-ctl transition-colors',
            on ? 'bg-control-on' : 'bg-control hover:bg-surface-hover',
          )}
        >
          <input
            type="checkbox"
            className={classNames(field.checkbox, 'mt-0.5 shrink-0', fade)}
            checked={on}
            onChange={() => onChange(CHANNELS.map((x) => x.id).filter((id) => (id === c.id ? !on : value.includes(id))))}
          />
          <span className={classNames('min-w-0 flex-1', fade)}>
            <span className="flex items-center gap-1.5 text-fg">
              <ChannelIcon channel={c.id} />
              {c.label}
            </span>
            {withNotes && <span className="mt-0.5 block text-xs text-fg-muted">{c.note}</span>}
          </span>
          {c.id === 'email' && <NotYetInfo what="email" nested className="mt-0.5" />}
        </label>
      );
    })}
  </div>
);

/**
 * Target segments (SEG-3): the team's segments and the countries' own (SEG-2), picked
 * apart, each as removable chips plus a select to add more. Each kind adds up, and the
 * two intersect: the leads in one of the segments and in one of the countries.
 */
export const SegmentPicker = ({ value, onChange, className }: { value: string[]; onChange: (v: string[]) => void; className?: string }) => {
  const { items, byId, error, loaded } = useSegments();
  // A segment not loaded yet, or deleted, shows with the team's.
  const picked = (countries: boolean) => value.filter((id) => Boolean(byId(id)?.country) === countries);
  const group = (countries: boolean) => {
    const ids = picked(countries);
    const rest = items.filter((s) => Boolean(s.country) === countries && !value.includes(s.id));
    const name = (s: (typeof items)[number]) => (countries ? countryName(s.country) : s.name);
    const add = countries ? 'Add a country' : 'Add a segment';
    return (
      <div>
        <p className={field.label}>{countries ? 'Countries' : 'Segments'}</p>
        <div className="flex flex-wrap items-center gap-2">
          {ids.map((id) => {
            const s = byId(id);
            return <SegmentChip key={id} name={s ? name(s) : segmentName(s, loaded, error)} color={s?.color} onRemove={() => onChange(value.filter((x) => x !== id))} />;
          })}
          {rest.length > 0 && (
            <select aria-label={add} className={classNames(field.select, 'w-full sm:w-auto')} value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])}>
              <option value="">{ids.length ? add : countries ? 'All countries' : 'Select segments'}</option>
              {rest.map((s) => (
                <option key={s.id} value={s.id}>
                  {name(s)} · {s.leads} leads
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    );
  };
  return (
    <div className={classNames('space-y-3', className)}>
      {group(false)}
      {items.some((s) => s.country) && group(true)}
      {picked(false).length > 0 && picked(true).length > 0 && <p className="text-xs text-fg-muted">Only the leads in one of the segments and in one of the countries.</p>}
      {error && <p className="text-xs text-danger">Segments could not be loaded.</p>}
      {loaded && !items.some((s) => !s.country) && (
        <p className="text-xs text-fg-muted">
          No segments yet.{' '}
          <Link to="/segments/new" className={btn.link}>
            Make one
          </Link>
        </p>
      )}
    </div>
  );
};

/** A segment chip's name: "Deleted segment" only once the segments are known. */
const segmentName = (s: { name: string } | undefined, loaded: boolean, error: boolean) => s?.name ?? (loaded ? 'Deleted segment' : error ? 'Segment not loaded' : '…');

/** Segments as chips, the first few only. */
export const SegmentList = ({ ids, max = 2, empty = '—' }: { ids: string[]; max?: number; empty?: ReactNode }) => {
  const { byId, loaded, error } = useSegments();
  if (!ids.length) return <span className="text-fg-faint">{empty}</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {ids.slice(0, max).map((id) => {
        const s = byId(id);
        return <SegmentChip key={id} name={segmentName(s, loaded, error)} color={s?.color} />;
      })}
      {ids.length > max && <span className="text-xs text-fg-faint">+{ids.length - max}</span>}
    </span>
  );
};

const STATUS: Record<MessageStatus, { label: string; tone: 'ok' | 'info' | 'neutral' | 'danger' }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  sending: { label: 'Sending', tone: 'info' },
  sent: { label: 'Sent', tone: 'ok' },
  failed: { label: 'Failed', tone: 'danger' },
};

export const MessageStatusBadge = ({ status }: { status: MessageStatus }) => <Badge tone={STATUS[status].tone}>{STATUS[status].label}</Badge>;

/** "Oct 20, 2026, 09:00 UTC", "For Oct 29, …", or "Edited Oct 26, 2026". */
export const messageDate = (m: Pick<Message, 'status' | 'sent_at' | 'scheduled_at' | 'updated_at'>) => {
  if (m.status === 'sent' && m.sent_at) return formatDateTime(m.sent_at);
  if ((m.status === 'scheduled' || m.status === 'sending') && m.scheduled_at) return `For ${formatDateTime(m.scheduled_at)}`;
  if (m.status === 'failed') return `Failed ${formatDateTime(m.updated_at)}`;
  return `Edited ${formatDate(m.updated_at)}`;
};

/** "12 sent (simulated) · 4 waiting · 1 failed", from the per-channel counts. */
export const deliverySummary = (m: Message) => {
  const all = Object.values(m.stats);
  if (!all.length) return '—';
  const sum = (k: 'sent' | 'simulated' | 'failed' | 'waiting' | 'read') => all.reduce((n, s) => n + (s?.[k] ?? 0), 0);
  const sent = sum('sent');
  const simulated = sum('simulated');
  const parts = [sent ? `${sent} sent${simulated === sent ? ' (simulated)' : simulated ? `, ${simulated} simulated` : ''}` : ''];
  if (sum('read')) parts.push(`${sum('read')} read`);
  if (sum('waiting')) parts.push(`${sum('waiting')} waiting`);
  if (sum('failed')) parts.push(`${sum('failed')} failed`);
  return parts.filter(Boolean).join(' · ') || '—';
};

/** One recipient's result on one channel: sent, simulated, failed or waiting; an Owner Hub notification also seen or read. */
export const ResultBadge = ({ result }: { result: DeliveryResult }) => {
  if (result.status === 'failed') {
    return (
      <Badge tone="danger" title={result.error}>
        Failed
      </Badge>
    );
  }
  if (result.status === 'waiting') return <Badge title={result.error}>Waiting</Badge>;
  if (result.simulated) return <Badge title="Nothing really left: no email provider yet, or the bot in simulate mode">Simulated</Badge>;
  if (result.read_at) {
    return (
      <Badge tone="ok" title={`Read ${formatDateTime(result.read_at)}`}>
        Read
      </Badge>
    );
  }
  if (result.shown_at) {
    return (
      <Badge tone="ok" title={`Seen ${formatDateTime(result.shown_at)}, not read yet`}>
        Seen
      </Badge>
    );
  }
  return <Badge tone="ok">Sent</Badge>;
};

export { Toggle } from '@/components/ui/controls';

/** Inserts {{variable}} tokens into a textarea at the cursor. */
export const useVariableInsert = (value: string, setValue: (v: string) => void) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? start;
    setValue(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  return { ref, insert };
};

/** Buttons that insert each variable a message can use. */
export const VariableBar = ({ onInsert }: { onInsert: (key: string) => void }) => (
  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fg-muted md:gap-1.5">
    Insert
    {VARIABLES.map((v) => (
      <button
        key={v.key}
        type="button"
        onClick={() => onInsert(v.key)}
        title={`${v.label}, for example ${v.sample}`}
        className="pill inline-flex min-h-11 items-center rounded-pill px-3 font-mono text-fg hover:[--tag-color:rgb(var(--c-surface-hover))] md:min-h-0 md:px-2 md:py-0.5"
      >
        {`{{${v.key}}}`}
      </button>
    ))}
  </div>
);

/** How a message looks on one channel, with the sample values filled in. */
export const ChannelPreview = ({ channel, title, body, linkLabel }: { channel: Channel; title: string; body: string; linkLabel?: string }) => {
  const heading = fillSample(title).trim();
  const text = fillSample(body).trim() || 'The text of the message shows here.';
  if (channel === 'email') {
    return (
      <div className="rounded-lg bg-surface-inset p-4 text-sm">
        <p className="text-xs text-fg-muted">Subject</p>
        <p className="text-fg-strong">{heading || 'No subject yet'}</p>
        <p className="mt-3 whitespace-pre-wrap text-fg">{text}</p>
      </div>
    );
  }
  if (channel === 'telegram') {
    return (
      <div className="rounded-lg bg-surface-inset p-4">
        <p className="mb-1 inline-flex items-center gap-1 text-xs text-fg-muted">
          <BotIcon />
          Prisma bot
        </p>
        <div className="max-w-[90%] whitespace-pre-wrap rounded-lg bg-info/15 px-3 py-2 text-sm text-fg">
          {heading && <span className="mb-1 block text-fg-strong">{heading}</span>}
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-center rounded-lg bg-surface-inset p-6">
      <div className="w-full max-w-xs rounded-card bg-surface p-4 shadow-e3">
        <p className="text-sm text-fg-strong">{heading || 'No title yet'}</p>
        <p className="mt-2 line-clamp-6 whitespace-pre-wrap text-sm text-fg-muted">{text}</p>
        <span className={classNames(btn.primary, 'pointer-events-none mt-4 w-full')}>{linkLabel?.trim() || 'Got it'}</span>
      </div>
    </div>
  );
};

/** The Owner Hub notification's button, when the message has a link: the team's text, else "Open". */
export const linkButtonLabel = (m: { link_url?: string; link_label?: string }) => (m.link_url ? m.link_label?.trim() || 'Open' : undefined);

/** The characters a URI may hold (RFC 3986), each %-escape complete. */
const URI_CHARS = /^(?:[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=]|%[0-9A-Fa-f]{2})+$/;

/**
 * What Owner Hub takes as a notification's link (the backend checks the same, msgLinkOK): an Owner Studio path
 * such as /wallet, with no spaces, control characters or backslashes, never //host; or an https:// address made
 * only of what a URI allows, with at most one #.
 */
export const linkOK = (link: string) => {
  if (!link) return true;
  // eslint-disable-next-line no-control-regex
  if (link.startsWith('/')) return !link.startsWith('//') && !/[\s\\\u0000-\u001f\u007f-\u009f]/.test(link);
  return /^https:\/\/[^/?#]/.test(link) && (link.match(/#/g) ?? []).length <= 1 && URI_CHARS.test(link);
};

/** The preview of a message on each of its channels, one at a time. */
export const PreviewPanel = ({ channels, title, body, linkLabel }: { channels: Channel[]; title: string; body: string; linkLabel?: string }) => {
  const [tab, setTab] = useState<Channel>(channels[0] ?? 'email');
  const current = channels.includes(tab) ? tab : channels[0];
  if (!current) return <p className="text-sm text-fg-faint">Pick a channel to see a preview.</p>;
  return (
    <div className="space-y-3">
      {channels.length > 1 && (
        <Segmented label="Preview channel" value={current} onChange={setTab} options={channels.map((c) => ({ value: c, label: channelName(c) }))} />
      )}
      <ChannelPreview channel={current} title={title} body={body} linkLabel={linkLabel} />
    </div>
  );
};

/**
 * Who these segments reach on each channel (POST /messages/audience), asked again a
 * moment after the segments or channels change.
 */
export const useAudience = (segments: string[], channels: Channel[]) => {
  const [state, setState] = useState<{ data: Audience | null; error: boolean; loading: boolean }>({ data: null, error: false, loading: false });
  const key = JSON.stringify([segments, channels]);
  useEffect(() => {
    if (!segments.length) {
      setState({ data: null, error: false, loading: false });
      return;
    }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      http
        .post<Audience>('/messages/audience', { segment_ids: segments, channels }, { signal: ctrl.signal })
        .then((r) => setState({ data: r.data, error: false, loading: false }))
        .catch(() => !ctrl.signal.aborted && setState({ data: null, error: true, loading: false }));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
    // `key` stands for the two arrays, which are new on every render.
  }, [key]);
  return state;
};

export const REACH: Record<Channel, string> = {
  email: 'people',
  telegram: 'people in a group with the bot',
  popup: 'people with an Owner Hub account',
};

/** Who a message reaches on each channel, the OUT-4 note, and the segments that cannot narrow it. */
export const AudienceList = ({ segments, channels }: { segments: string[]; channels: Channel[] }) => {
  const audience = useAudience(segments, channels);
  if (!segments.length) return <p className="text-sm text-fg-faint">Pick a segment to see who this reaches.</p>;
  if (!channels.length) return <p className="text-sm text-fg-faint">Pick a channel to see who this reaches.</p>;
  if (audience.error) return <p className="text-sm text-danger">The audience could not be counted.</p>;
  const a = audience.data;
  if (!a) return <p className="text-sm text-fg-faint">Counting…</p>;
  return (
    <div className={classNames('transition-opacity', audience.loading && 'opacity-60')}>
      <dl>
        {channels.map((c) => (
          <Field key={c} label={channelName(c)}>
            <span className="tabular-nums">{a.channels[c] ?? 0}</span> {REACH[c]}
          </Field>
        ))}
      </dl>
      {channels.length > 1 && <p className="mt-2 text-xs text-fg-muted">One person can get it on several channels. Prisma does not deduplicate across channels (OUT-4).</p>}
      <SkippedWarnings audience={a} />
    </div>
  );
};

/** The segments with rules that cannot be counted yet; the ones that would reach everyone block sending. */
export const SkippedWarnings = ({ audience }: { audience: Audience }) => {
  const affected = audience.segments.filter((s) => s.skipped > 0 || s.reaches_everyone);
  if (!affected.length) return null;
  return (
    <ul className="mt-3 space-y-1.5">
      {affected.map((s) => (
        <li key={s.id} className={classNames('rounded-lg px-3 py-2 text-xs', s.reaches_everyone ? 'bg-danger/10 text-danger' : 'bg-warn/15 text-fg')}>
          {s.reaches_everyone && s.rules === 0
            ? `“${s.name}” has no conditions, so it would reach every lead. Add one to send to it.`
            : s.reaches_everyone
              ? `“${s.name}” has no rule Prisma can count yet, so it would reach every lead. It cannot be sent to until its rules have data.`
              : `“${s.name}”: ${s.skipped} of ${s.rules} rules have no data yet and narrow nothing.`}
        </li>
      ))}
    </ul>
  );
};

/**
 * The bottom of an editor: its notices and buttons. On a phone it sticks just above
 * the bottom tabs, so Save or Send is always in reach (UI-2); on a desktop it is the
 * usual row under the form.
 */
export const ActionBar = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={classNames(
      'sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 -mx-4 mt-6 bg-surface px-4 py-3 shadow-up',
      'md:static md:z-auto md:mx-0 md:bg-transparent md:px-0 md:pb-0 md:pt-4',
      className,
    )}
  >
    {children}
  </div>
);

/** ISO time to the value a datetime-local input wants (local time), and back. */
export const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
export const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

const EVENT_ICONS: Record<TriggerEvent, ReactNode> = {
  casino_launched: <path d="M8 1.5l5.5 3v7L8 14.5l-5.5-3v-7L8 1.5zM2.5 4.5L8 7.5l5.5-3M8 7.5v7" />,
  no_players: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </>
  ),
  lead_added: (
    <>
      <circle cx="6.5" cy="5.5" r="2.5" />
      <path d="M1.5 14c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5M13 4.5v4M11 6.5h4" />
    </>
  ),
  first_message: <path d="M2 3.5h12v7.5H6l-3.5 2.5V11H2z" />,
  stage_changed: <path d="M2 13h3.5V9.5H9V6h3.5V2.5H14" />,
};

/** A round icon for what starts a trigger (OUT-3). */
export const TriggerIcon = ({ event }: { event: TriggerEvent }) => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {EVENT_ICONS[event]}
    </svg>
  </span>
);

/** A centred "no such thing" with a way back. */
export const NotFound = ({ what, back, label }: { what: string; back: string; label: string }) => (
  <StateMessage title={`No such ${what}`}>
    <Link to={back} className={btn.link}>
      {label}
    </Link>
  </StateMessage>
);

/** The status of an API answer, for the "it did not work" lines under a form. */
export const errorStatus = (err: unknown) => (err as { response?: { status?: number } }).response?.status;
